import React, { useEffect, useRef } from "react";
import PromotedTag from "~/common/PromotedTag/PromotedTag";
import connectUserAndChat, { User } from "~/common/Store/connectUserAndChat";
import { BrandSpotlightAdEnhancedGridResult } from "~/common/Store/models/Category";
import { addPhotoGridPromotedBrandSpotlightToSessionStorage } from "~/productListing/addAdsToSessionStorage";
import PromotedBrandAdType from "~/productListing/PromotedBrandAdType";
import { trackAdService } from "~/common/services/AdvertisementTracking/trackAdService";
import { BrandAdRequest } from "~/common/services/AdvertisementTracking/types";

export interface BrandLogoImage {
  small: string;
  medium: string;
  extraLarge: string;
}

export interface Props {
  adId: number;
  brandIds: number[] | undefined;
  brandName: string | undefined;
  link: string | undefined;
  images: BrandLogoImage | undefined;
  enhancedGrid: BrandSpotlightAdEnhancedGridResult | undefined;
  user: User;
  mounted: boolean;
}

const BrandSpotlightAd = ({
  adId,
  brandIds,
  brandName,
  link,
  images,
  enhancedGrid,
  user,
  mounted,
}: Props) => {
  const requestRef = useRef<BrandAdRequest>({
    adId: -1,
    userId: null,
    sessionId: undefined,
    promotedBrandAdType: PromotedBrandAdType.PhotoGridPromotedBrandSpotlight,
    url: undefined,
  });

  const observerRef = useRef<IntersectionObserver | null>(null);
  const adRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (observerRef.current !== null) {
        observerRef.current.disconnect();
      }
    };
  }, [observerRef]);

  useEffect(() => {
    requestRef.current.userId = Number(user.index) || null;
    requestRef.current.adId = adId;
    requestRef.current.promotedBrandAdType =
      PromotedBrandAdType.PhotoGridPromotedBrandSpotlight;
    requestRef.current.url = link;

    if (mounted && adRef.current !== null && observerRef.current === null) {
      const observerCallback = (
        observations: IntersectionObserverEntry[],
        observer: IntersectionObserver,
      ) => {
        const observation = observations[0];
        if (
          observation !== undefined &&
          observation.isIntersecting &&
          observation.intersectionRatio >= 0.8
        ) {
          trackAdService("brandads", "impression", requestRef.current);

          observer.disconnect();
        }
      };

      const observerOptions: IntersectionObserverInit = {
        root: null,
        rootMargin: "0px",
        threshold: 0.8,
      };

      observerRef.current = new IntersectionObserver(
        observerCallback,
        observerOptions,
      );

      observerRef.current.observe(adRef.current);
    }
  }, [adId, link, mounted, user.index]);

  const onAdClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const link = e.currentTarget.getAttribute("href") || "";
    addPhotoGridPromotedBrandSpotlightToSessionStorage(
      requestRef.current.adId,
      brandIds,
    );
    requestRef.current.url = link;
    await trackAdService("brandads", "click", requestRef.current);

    window.location.href = link;
  };

  const { sections } = {
    ...enhancedGrid,
  };

  return (
    <>
      <div
        className="border-t-0 border-b-[0.5px] md:border-y border-x-0 border-gray-200 border-solid"
        style={{ margin: "0 -10px" }}
        ref={adRef}
      >
        <div className="flex flex-row relative">
          <div
            className="absolute right-3 pt-3 pr-3 flex items-center"
            id="brandSpotlightAd"
          >
            <PromotedTag />
          </div>
        </div>
        <div className="flex flex-col justify-center p-5 md:flex-row md:items-end md:px-4 md:py-10 lg:items-center lg:py-12 xl:px-8">
          <div className="brand-logo flex flex-col md:w-[30%] xl:flex-row xl:w-auto">
            <div className="self-start pt-1 w-40 xl:self-center xl:pr-6">
              <img
                className="xl:w-full w-24"
                src={images?.medium}
                alt=""
                style={{ mixBlendMode: "multiply" }}
              />
            </div>
            <span className="hidden xl:border-gray-200 xl:border-solid xl:border xl:block" />
            <div className="self-start pb-4 lg:pt-1 xl:self-center xl:pt-2 xl:pl-6">
              <h3 className="font-bold text-xl leading-6 mb-2 pt-2 xl:pt-0">
                {brandName}
              </h3>
              <a
                onClick={onAdClick}
                href={link}
                className="bg-origin-box-border bg-repeat-x border-solid border box-border cursor-pointer inline-block text-center no-underline hover:no-underline antialiased hover:bg-position-y-15 rounded text-xs leading-3 px-3 py-1-1/2 bg-transparent hover:bg-green-500 border-green-500 text-green-500 hover:text-white align-middle font-semibold"
              >
                Shop All
              </a>
            </div>
          </div>
          <div className="grid max-w-4xl gap-1 grid-cols-4 grid-rows-1 md:pl-5 md:w-[70%] md:gap-2 lg:ml-2 lg:pl-6 lg:gap-4">
            {sections &&
              sections
                .sort((a, b) => a.order - b.order)
                .map((section, index) => (
                  <div
                    className="mb-0 pt-2 px-1 font-semibold text-center text-xs leading-4 sm:text-sm"
                    key={section.description}
                  >
                    <a
                      onClick={onAdClick}
                      className="section-link"
                      href={section.link}
                      data-testid={`brandspotlightAd${index}`}
                    >
                      <img
                        className="bg-white rounded max-w-full inset-0"
                        src={section.imagePath}
                        alt=""
                      />
                      <span className="block mt-1 leading-none break-words">
                        {section.description}
                      </span>
                    </a>
                  </div>
                ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default connectUserAndChat(BrandSpotlightAd);
